<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddAttribute from './AddAttribute.vue';
import EditAttribute from './EditAttribute.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  attributeModel: {
    type: String,
    default: 'contact_attribute',
  },
});

const { t } = useI18n();

const getters = useStoreGetters();
const store = useStore();
const { currentAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const [showAddPopup, toggleAddPopup] = useToggle(false);
const searchQuery = ref('');
const uiFlags = computed(() => getters['attributes/getUIFlags'].value);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);
const selectedAttribute = ref({});

const systemContactFields = [
  {
    id: 'system-name',
    attribute_display_name: 'Name',
    attribute_key: 'name',
    attribute_description: "The contact's display name",
    attribute_display_type: 'Text',
    source: 'system',
  },
  {
    id: 'system-email',
    attribute_display_name: 'Email Address',
    attribute_key: 'email',
    attribute_description: "The contact's email address",
    attribute_display_type: 'Email',
    source: 'system',
  },
  {
    id: 'system-phone',
    attribute_display_name: 'Phone Number',
    attribute_key: 'phone_number',
    attribute_description: 'Phone numbers are stored in E.164 format.',
    attribute_display_type: 'Phone',
    source: 'system',
  },
  {
    id: 'system-profile-picture',
    attribute_display_name: 'Profile Picture',
    attribute_key: 'thumbnail',
    attribute_description: "The contact's profile picture",
    attribute_display_type: 'Image',
    source: 'system',
  },
  {
    id: 'system-country',
    attribute_display_name: 'Country',
    attribute_key: 'country',
    attribute_description: "The contact's country",
    attribute_display_type: 'Text',
    source: 'system',
  },
  {
    id: 'system-city',
    attribute_display_name: 'City',
    attribute_key: 'city',
    attribute_description: "The contact's city",
    attribute_display_type: 'Text',
    source: 'system',
  },
  {
    id: 'system-language',
    attribute_display_name: 'Language',
    attribute_key: 'language',
    attribute_description: "The contact's language",
    attribute_display_type: 'Text',
    source: 'system',
  },
  {
    id: 'system-tags',
    attribute_display_name: 'Tags',
    attribute_key: 'labels',
    attribute_description: 'Labels assigned to the contact',
    attribute_display_type: 'MultiValue',
    source: 'system',
  },
  {
    id: 'system-lifecycle',
    attribute_display_name: 'Lifecycle Stage',
    attribute_key: 'lifecycle_stage',
    attribute_description: "The contact's current lifecycle stage",
    attribute_display_type: 'List',
    source: 'system',
  },
];

const openAddPopup = () => {
  toggleAddPopup(true);
};
const hideAddPopup = () => {
  toggleAddPopup(false);
};
const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
};

onMounted(() => {
  store.dispatch('attributes/get');
});

const attributeModel = computed(() => props.attributeModel);

const selectedAttributeModelTab = computed(() =>
  attributeModel.value === 'contact_attribute' ? 1 : 0
);

const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value(attributeModel.value)
);

const handleEditAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleEditPopup(true);
};

const handleDeleteAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleDeletePopup(true);
};

const confirmDeleteAttribute = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDelete();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const requiredAttributeKeys = computed(
  () => currentAccount.value?.settings?.conversation_required_attributes || []
);

const hasPreChatBadge = attribute => {
  return (inboxes.value || []).some(inbox => {
    const fields =
      inbox?.pre_chat_form_options?.pre_chat_fields ||
      inbox?.channel?.pre_chat_form_options?.pre_chat_fields ||
      [];
    return fields.some(field => field.name === attribute.attribute_key);
  });
};

const buildBadges = attribute => {
  const badges = [];
  if (hasPreChatBadge(attribute)) {
    badges.push({
      type: 'pre-chat',
    });
  }

  if (
    attribute.attribute_model === 'conversation_attribute' &&
    requiredAttributeKeys.value.includes(attribute.attribute_key)
  ) {
    badges.push({
      type: 'resolution',
    });
  }

  return badges;
};

const derivedAttributes = computed(() =>
  attributes.value.map(attribute => ({
    ...attribute,
    source: 'custom',
    label: attribute.attribute_display_name,
    type: attribute.attribute_display_type,
    value: attribute.attribute_key,
    badges: buildBadges(attribute),
  }))
);

const displayedAttributes = computed(() => {
  if (attributeModel.value === 'contact_attribute') {
    return [...systemContactFields, ...derivedAttributes.value];
  }
  return derivedAttributes.value;
});

const filteredAttributes = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return displayedAttributes.value;
  return picoSearch(displayedAttributes.value, query, [
    'attribute_display_name',
    'attribute_key',
    'attribute_description',
  ]);
});

const isContactTab = computed(
  () => attributeModel.value === 'contact_attribute'
);

const headerTitle = computed(() =>
  isContactTab.value
    ? t('ATTRIBUTES_MGMT.CONTACT_FIELDS.HEADER')
    : t('ATTRIBUTES_MGMT.CONVERSATION_FIELDS.HEADER')
);

const headerDescription = computed(() =>
  isContactTab.value
    ? t('ATTRIBUTES_MGMT.CONTACT_FIELDS.DESCRIPTION')
    : t('ATTRIBUTES_MGMT.CONVERSATION_FIELDS.DESCRIPTION')
);

const actionLabel = computed(() =>
  isContactTab.value
    ? t('ATTRIBUTES_MGMT.CONTACT_FIELDS.ADD_BUTTON')
    : t('ATTRIBUTES_MGMT.CONVERSATION_FIELDS.ADD_BUTTON')
);

const tableHeaders = computed(() => [
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.NAME'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.KEY'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.TYPE'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.SOURCE'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.ACTIONS'),
]);

const canManageAttribute = attribute => attribute.source === 'custom';

watch(attributeModel, () => {
  searchQuery.value = '';
  hideAddPopup();
  hideEditPopup();
  closeDelete();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="headerTitle"
        :description="headerDescription"
        :link-text="$t('ATTRIBUTES_MGMT.LEARN_MORE')"
        :search-placeholder="$t('ATTRIBUTES_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="custom_attributes"
      >
        <template v-if="attributes?.length" #count>
          <span class="text-body-main text-n-slate-11 truncate min-w-0">
            {{ $t('ATTRIBUTES_MGMT.COUNT', { n: attributes.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="actionLabel"
            icon="i-lucide-plus"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <span
          v-if="!filteredAttributes.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('ATTRIBUTES_MGMT.NO_RESULTS') }}
        </span>
        <div
          v-else-if="filteredAttributes.length"
          class="overflow-x-auto border-t border-n-weak"
        >
          <table class="min-w-full table-fixed divide-y divide-n-weak">
            <thead class="bg-n-alpha-2">
              <tr>
                <th
                  v-for="header in tableHeaders"
                  :key="header"
                  class="px-3 py-3 text-sm font-medium text-start text-n-slate-11"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="attribute in filteredAttributes"
                :key="attribute.id"
                class="text-sm text-n-slate-12"
              >
                <td class="px-3 py-3 font-medium truncate">
                  {{ attribute.attribute_display_name || attribute.label }}
                </td>
                <td class="px-3 py-3 truncate text-n-slate-11">
                  {{ attribute.attribute_key || attribute.value }}
                </td>
                <td class="px-3 py-3 truncate text-n-slate-11">
                  {{ attribute.attribute_description || 'N/A' }}
                </td>
                <td class="px-3 py-3 truncate text-n-slate-11">
                  {{ attribute.attribute_display_type || attribute.type }}
                </td>
                <td class="px-3 py-3 truncate text-n-slate-11">
                  {{
                    canManageAttribute(attribute)
                      ? $t('ATTRIBUTES_MGMT.LIST.SOURCE.CUSTOM')
                      : $t('ATTRIBUTES_MGMT.LIST.SOURCE.SYSTEM')
                  }}
                </td>
                <td class="px-3 py-3">
                  <div class="flex items-center gap-2">
                    <Button
                      v-if="canManageAttribute(attribute)"
                      icon="i-woot-edit-pen"
                      slate
                      sm
                      @click="handleEditAttribute(attribute)"
                    />
                    <Button
                      v-if="canManageAttribute(attribute)"
                      icon="i-woot-bin"
                      slate
                      sm
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      @click="handleDeleteAttribute(attribute)"
                    />
                    <span
                      v-else
                      class="inline-flex items-center gap-1 text-xs text-n-slate-10"
                    >
                      <Icon icon="i-lucide-lock" class="size-3" />
                      {{ $t('ATTRIBUTES_MGMT.LIST.SYSTEM_FIELD') }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-else
          class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
        >
          {{ $t('ATTRIBUTES_MGMT.LIST.EMPTY_RESULT.404') }}
        </p>
      </div>
    </template>
    <AddAttribute
      v-if="showAddPopup"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
      :selected-attribute-model-tab="selectedAttributeModelTab"
      lock-attribute-model
    />
    <woot-modal v-model:show="showEditPopup" @close="hideEditPopup">
      <EditAttribute
        :selected-attribute="selectedAttribute"
        :is-updating="uiFlags.isUpdating"
        @on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      :message="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="`${$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.YES')} ${
        selectedAttribute.attribute_display_name || ''
      }`"
      :reject-text="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.NO')"
      :confirm-value="selectedAttribute.attribute_display_name"
      :confirm-place-holder-text="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      @on-confirm="confirmDeleteAttribute"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
